import type { Ref } from 'vue';

import type { ArmOf, ExperimentDefinition } from '~/utils/experiments/defineExperiment';

import { useAnalyticsService } from '~/composables/core';
import { useDevModeGate } from '~/composables/unified/DevPanel/useDevModeGate';
import { useFeatureFlags } from '~/composables/useFeatureFlags';
import { EVENTS } from '~/constants/events';
import { readExperimentOverride } from '~/utils/experiments/defineExperiment';

/** What one page load already knows about an experiment: the arm, and whether Unleash drew it. */
interface ArmResolution {
  arm: string;
  bucketed: boolean;
}

/**
 * The current user's arm in an experiment, and the exposure that goes with it.
 *
 * `arm` starts as the control arm and stays there for everyone the experiment does not cover: the
 * flag off, the user outside the rollout, a variant name the definition does not know (logged, so
 * a typo in the Unleash console shows up), or a server render. UI code switches on `arm` alone.
 * `enrolled` says whether Unleash actually bucketed this user, which is when an exposure is sent.
 *
 * Resolving the arm and counting the exposure are two steps. `prefetch()` asks Unleash and settles
 * `arm` without reporting anything, so a surface drawn later starts on the right arm instead of
 * flipping from control once the answer lands. `enrol()` resolves too if nobody has, then reports
 * the exposure; call it where the user is in front of the surface. The answer is kept per page
 * load, so a second `useExperiment` of the same definition starts on the arm the first one drew.
 *
 * The exposure is Mixpanel's own `$experiment_started` event, the one its Experiments report
 * reads, carrying the experiment and variant names. It goes out once per experiment and arm per
 * page load; Mixpanel keeps the first per user.
 *
 * Unleash buckets on `userId`, so call `prefetch` or `enrol` only once the user exists (a ghost
 * counts), or the same person can land in a different arm on their next visit. Behind the
 * dev-mode gate, `?experiment=<flag>:<arm>` forces an arm without an exposure, for checking each
 * arm by hand.
 */
export const useExperiment = <const D extends ExperimentDefinition>(experiment: D) => {
  type Arm = ArmOf<D>;

  const { getVariant } = useFeatureFlags();
  const analytics = useAnalyticsService();
  const route = useRoute();
  const { $logWarnMessage } = useNuxtApp();
  const reported = useState<string[]>('experiment-exposures', () => []);
  const resolutions = useState<Record<string, ArmResolution>>('experiment-arms', () => ({}));

  const isArm = (name: string): name is Arm => experiment.arms.includes(name);

  const known = resolutions.value[experiment.flag];
  const arm = ref(known && isArm(known.arm) ? known.arm : experiment.control) as Ref<Arm>;
  const enrolled = ref(false);
  let bucketed = known?.bucketed ?? false;
  let resolution: Promise<Arm> | null = known ? Promise.resolve(arm.value) : null;

  const reportExposure = (name: Arm) => {
    const exposure = `${experiment.flag}:${name}`;
    if (reported.value.includes(exposure)) {
      return;
    }
    reported.value = [...reported.value, exposure];
    analytics.track(EVENTS.EXPERIMENT_STARTED, {
      'Experiment name': experiment.flag,
      'Variant name': name,
    });
  };

  const settle = (name: Arm, drawn: boolean) => {
    arm.value = name;
    bucketed = drawn;
    resolutions.value = { ...resolutions.value, [experiment.flag]: { arm: name, bucketed: drawn } };

    return name;
  };

  const resolveArm = async (): Promise<Arm> => {
    if (import.meta.server) {
      return arm.value;
    }

    const override = readExperimentOverride({ query: route.query, experiment });
    if (override !== null && await useDevModeGate()) {
      return settle(override, false);
    }

    const variant = await getVariant(experiment.flag);
    if (!variant?.enabled || !variant.name) {
      return settle(arm.value, false);
    }
    if (!isArm(variant.name)) {
      $logWarnMessage(`Experiment ${experiment.flag} answered a variant it does not define: ${variant.name}`);

      return settle(arm.value, false);
    }

    return settle(variant.name, true);
  };

  /** Resolves the arm once, with no exposure; later calls return the same answer. */
  const prefetch = (): Promise<Arm> => {
    resolution ??= resolveArm();

    return resolution;
  };

  /** Resolves the arm if nobody has yet, then counts the exposure. */
  const enrol = async (): Promise<Arm> => {
    const name = await prefetch();
    if (bucketed && !enrolled.value) {
      enrolled.value = true;
      reportExposure(name);
    }

    return name;
  };

  /** Properties to stamp on events fired inside the experiment, so each one says which arm saw it. */
  const experimentProps = () => ({
    experiment: experiment.flag,
    experimentVariant: enrolled.value ? arm.value : null,
  });

  return {
    arm: readonly(arm) as Readonly<Ref<Arm>>,
    enrolled: readonly(enrolled),
    prefetch,
    enrol,
    experimentProps,
  };
};
